// API Client - abstraction layer for all data access
// Currently uses mock data from the universe generator, but interface is designed for easy swap to real HTTP

import type {
  Device,
  DeviceStatus,
  DeviceWithStatus,
  OrgStats,
  OrgWithStats,
  MspDashboardStats,
  DeviceDetail,
  ApiResponse,
  ListDevicesParams,
  ListOrgsParams,
  NetworkQuality,
  NetworkQualityStatus,
  MSP,
  NetworkSnapshot,
  Finding,
  FindingsResponse,
} from './types';

import { getDefaultUniverse } from './universeGenerator';

// Re-export types for convenience
export type * from './types';
export { regenerateUniverse, type UniverseConfig } from './universeGenerator';

// Session/Auth types
export interface SessionInfo {
  authenticated: boolean;
  mspId?: string;
  email?: string;
  name?: string;
  picture?: string;
  provider?: 'google' | 'microsoft';
  expiresAt?: string;
  setupComplete?: boolean;
  suggestedOrgName?: string;
}

export interface AuthConfig {
  googleClientId: string;
  microsoftClientId: string;
}

// Device log entry from Cloud Logging
export interface DeviceLogEntry {
  timestamp: string;
  severity: string;
  payload: Record<string, unknown>;
}

export interface DeviceLogsResponse {
  data: DeviceLogEntry[];
  meta: { total: number };
}

// MSP member (identity linked to an MSP)
export interface MspMember {
  email: string;
  name: string;
  picture?: string;
  provider: string;
  lastLoginAt: string;
}

export interface MspMembersResponse {
  data: MspMember[];
  meta: { total: number };
}

// Token-based auth: check URL ?token= param, persist to localStorage
function initBearerToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  if (urlToken) {
    localStorage.setItem('nwx_api_token', urlToken);
    // Remove token from URL to avoid leaking it in bookmarks/history
    params.delete('token');
    const clean = params.toString();
    const newUrl = window.location.pathname + (clean ? '?' + clean : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
    return urlToken;
  }
  return localStorage.getItem('nwx_api_token');
}

let bearerToken: string | null = initBearerToken();

// Configuration
const config = {
  // When true, uses mock data. When false, makes real API calls.
  // Default is false (real API) - must explicitly opt into mock mode
  useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true',
  // Base URL for real API (used when useMockData is false)
  baseUrl: import.meta.env.VITE_API_BASE_URL || '',
};

// Get latest version from universe
function getLatestVersion(): string {
  return getDefaultUniverse().latestVersion;
}

// Status calculation logic (shared between mock and real implementations)
export function calculateDeviceStatus(device: Device): DeviceStatus {
  // Use lastSeen, fallback to lastCheckIn, fallback to very old date
  const lastSeenStr = device.lastSeen ?? device.lastCheckIn ?? '1970-01-01T00:00:00Z';
  const lastSeenDate = new Date(lastSeenStr);
  const now = new Date();
  const hoursSinceLastSeen = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60 * 60);

  const isCurrentVersion = device.currentVersion === getLatestVersion();

  if (hoursSinceLastSeen > 24) {
    return 'critical';
  }

  if (hoursSinceLastSeen > 1 || !isCurrentVersion) {
    return 'warning';
  }

  return 'healthy';
}

// Network quality thresholds
const networkThresholds = {
  latency: { good: 50, degraded: 150 },     // ms
  loss: { good: 1, degraded: 5 },           // percent
  jitter: { good: 10, degraded: 30 },       // ms
};

export function calculateNetworkQualityStatus(
  latencyMs: number,
  lossPercent: number,
  jitterMs: number
): NetworkQualityStatus {
  // Any metric in "poor" range = poor
  if (latencyMs > networkThresholds.latency.degraded ||
      lossPercent > networkThresholds.loss.degraded ||
      jitterMs > networkThresholds.jitter.degraded) {
    return 'poor';
  }
  // Any metric in "degraded" range = degraded
  if (latencyMs > networkThresholds.latency.good ||
      lossPercent > networkThresholds.loss.good ||
      jitterMs > networkThresholds.jitter.good) {
    return 'degraded';
  }
  return 'good';
}

// Generate mock network quality based on device's network context
function generateMockNetworkQuality(device: Device): NetworkQuality | null {
  // Offline devices have no recent measurements
  if (device.connectionState === 'offline') {
    return null;
  }

  // Seed randomness based on clientId for consistent mock data
  const seed = device.clientId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const pseudoRandom = (offset: number) => ((seed + offset) % 100) / 100;

  // Base quality on network type and location
  let baseLatency = 25;
  let baseLoss = 0.2;
  let baseJitter = 5;

  // Adjust based on network type (if network context is available)
  const networkType = device.network?.networkType ?? 'unknown';
  switch (networkType) {
    case 'corporate':
      // Corporate networks are typically well-managed
      if (device.network?.radiusAuthenticated) {
        baseLatency = 12;
        baseLoss = 0;
        baseJitter = 2;
      } else {
        // Guest/public corporate networks are less reliable
        baseLatency = 35;
        baseLoss = 0.8;
        baseJitter = 12;
      }
      break;
    case 'residential': {
      // Home networks vary by ISP
      const ispName = device.network?.ispName ?? '';
      if (ispName.includes('Fiber') || ispName.includes('AT&T')) {
        baseLatency = 18;
        baseLoss = 0.2;
        baseJitter = 5;
      } else if (ispName.includes('CenturyLink') || ispName.includes('DSL')) {
        baseLatency = 65;
        baseLoss = 2.5;
        baseJitter = 25;
      } else {
        // Cable (Comcast, etc)
        baseLatency = 35;
        baseLoss = 0.8;
        baseJitter = 12;
      }
      break;
    }
    case 'mobile':
      // Mobile networks are variable
      baseLatency = 55;
      baseLoss = 1.8;
      baseJitter = 22;
      break;
    default:
      baseLatency = 40;
      baseLoss = 1.0;
      baseJitter = 15;
  }

  // Idle connections typically have stale data
  if (device.connectionState === 'idle') {
    baseLatency *= 1.5;
    baseLoss *= 2;
    baseJitter *= 1.5;
  }

  // Add per-device variance (±20%)
  const latencyMs = Math.round(baseLatency * (0.8 + pseudoRandom(1) * 0.4));
  const lossPercent = Math.round((baseLoss * (0.5 + pseudoRandom(2) * 1.0)) * 10) / 10;
  const jitterMs = Math.round(baseJitter * (0.7 + pseudoRandom(3) * 0.6));

  return {
    latencyMs,
    lossPercent,
    jitterMs,
    status: calculateNetworkQualityStatus(latencyMs, lossPercent, jitterMs),
    measuredAt: device.lastTelemetry ?? device.lastCheckIn,
  };
}

export function enrichDeviceWithStatus(device: Device): DeviceWithStatus {
  return {
    ...device,
    status: calculateDeviceStatus(device),
    isCurrentVersion: device.currentVersion === getLatestVersion(),
    networkQuality: generateMockNetworkQuality(device),
  };
}

export function formatLastSeen(isoString: string | null | undefined): string {
  if (!isoString) return '--';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '--';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

// =============================================================================
// API Client Class (uses Universe Generator for mock data)
// =============================================================================

class MspApiClient {
  private baseUrl: string;
  private useMock: boolean;

  constructor() {
    this.baseUrl = config.baseUrl;
    this.useMock = config.useMockData;
  }

  // Get data from the generated universe
  private get universe() {
    return getDefaultUniverse();
  }

  // ---------------------------------------------------------------------------
  // Authentication
  // ---------------------------------------------------------------------------

  async getSession(): Promise<SessionInfo> {
    if (this.useMock) {
      // In mock mode, pretend we're authenticated
      return {
        authenticated: true,
        mspId: this.universe.msps[0].mspId,
        email: 'admin@techmsp.io',
        name: 'Admin User',
        provider: 'google',
      };
    }

    const headers: Record<string, string> = {};
    if (bearerToken) {
      headers['Authorization'] = `Bearer ${bearerToken}`;
    }

    const response = await fetch(`${this.baseUrl}/auth/session`, {
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    return response.json();
  }

  async logout(): Promise<void> {
    if (this.useMock) {
      return;
    }

    bearerToken = null;
    localStorage.removeItem('nwx_api_token');

    await fetch(`${this.baseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  }

  getGoogleLoginUrl(redirect?: string): string {
    const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
    return `${this.baseUrl}/auth/google/login${params}`;
  }

  getMicrosoftLoginUrl(redirect?: string): string {
    const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
    return `${this.baseUrl}/auth/microsoft/login${params}`;
  }

  async googleOneTapLogin(credential: string): Promise<{ authenticated: boolean; mspId?: string; isNewAccount?: boolean }> {
    if (this.useMock) {
      return { authenticated: true, mspId: this.universe.msps[0].mspId, isNewAccount: false };
    }

    const response = await fetch(`${this.baseUrl}/auth/google/one-tap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(err.error || 'Google One Tap login failed');
    }

    return response.json();
  }

  async getAuthConfig(): Promise<AuthConfig> {
    if (this.useMock) {
      return { googleClientId: '', microsoftClientId: '' };
    }

    const response = await fetch(`${this.baseUrl}/auth/config`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return { googleClientId: '', microsoftClientId: '' };
    }

    return response.json();
  }

  async setupMsp(name: string): Promise<ApiResponse<MSP>> {
    if (this.useMock) {
      const msp = this.universe.msps[0];
      return { data: { ...msp, name, setupComplete: true } };
    }

    const response = await fetch(`${this.baseUrl}/v1/msp/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Setup failed' }));
      throw new Error(err.message || 'Failed to set up organization');
    }

    return response.json();
  }

  // ---------------------------------------------------------------------------
  // MSP & Auth
  // ---------------------------------------------------------------------------

  async getCurrentMsp(): Promise<ApiResponse<MSP>> {
    if (this.useMock) {
      const msp = this.universe.msps[0];
      return { data: msp };
    }
    return this.fetch<MSP>('/v1/msp/current');
  }

  // ---------------------------------------------------------------------------
  // Organizations
  // ---------------------------------------------------------------------------

  async listOrganizations(params: ListOrgsParams): Promise<ApiResponse<OrgWithStats[]>> {
    if (this.useMock) {
      const orgs = this.universe.organizations.filter(o => o.mspId === params.mspId);
      const orgsWithStats: OrgWithStats[] = orgs.map(org => {
        const stats = this.calculateOrgStats(org.orgId);
        const overallHealth: DeviceStatus =
          stats.critical > 0 ? 'critical' :
          stats.warning > 0 ? 'warning' : 'healthy';
        return { ...org, stats, overallHealth };
      });
      return {
        data: orgsWithStats,
        meta: { total: orgsWithStats.length }
      };
    }
    return this.fetch<OrgWithStats[]>('/v1/organizations', { params: params as unknown as Record<string, unknown> });
  }

  async createOrganization(name: string): Promise<ApiResponse<{ orgId: string; name: string; mspId: string; slug: string }>> {
    if (this.useMock) {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const orgId = `org-${slug}-${Date.now() % 10000}`;
      const mspId = this.universe.msps[0].mspId;
      return { data: { orgId, name, mspId, slug } };
    }

    const response = await fetch(`${this.baseUrl}/v1/organizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to create organization' }));
      throw new Error(err.message || 'Failed to create organization');
    }

    return response.json();
  }

  async deleteOrganization(orgId: string): Promise<void> {
    if (this.useMock) {
      return;
    }

    const response = await fetch(`${this.baseUrl}/v1/organizations/${orgId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to delete organization' }));
      throw new Error(err.message || 'Failed to delete organization');
    }
  }

  async getOrganization(orgId: string): Promise<ApiResponse<OrgWithStats>> {
    if (this.useMock) {
      const org = this.universe.organizations.find(o => o.orgId === orgId);
      if (!org) throw new Error(`Organization not found: ${orgId}`);
      const stats = this.calculateOrgStats(orgId);
      const overallHealth: DeviceStatus =
        stats.critical > 0 ? 'critical' :
        stats.warning > 0 ? 'warning' : 'healthy';
      return { data: { ...org, stats, overallHealth } };
    }
    return this.fetch<OrgWithStats>(`/v1/organizations/${orgId}`);
  }

  // ---------------------------------------------------------------------------
  // Devices
  // ---------------------------------------------------------------------------

  async listDevices(params: ListDevicesParams): Promise<ApiResponse<DeviceWithStatus[]>> {
    if (this.useMock) {
      let devices = [...this.universe.devices];

      // Filter by org
      if (params.orgId) {
        devices = devices.filter(d => d.orgId === params.orgId);
      }

      // Filter by MSP
      if (params.mspId) {
        devices = devices.filter(d => d.mspId === params.mspId);
      }

      // Enrich with status
      let enriched = devices.map(enrichDeviceWithStatus);

      // Filter by status
      if (params.status) {
        enriched = enriched.filter(d => d.status === params.status);
      }

      // Sort
      const sortBy = params.sortBy || 'status';
      const sortOrder = params.sortOrder || 'asc';
      enriched.sort((a, b) => {
        let cmp = 0;
        switch (sortBy) {
          case 'status': {
            const order = { critical: 0, warning: 1, healthy: 2 };
            cmp = order[a.status] - order[b.status];
            break;
          }
          case 'lastSeen': {
            const aTime = new Date(a.lastSeen ?? a.lastCheckIn).getTime();
            const bTime = new Date(b.lastSeen ?? b.lastCheckIn).getTime();
            cmp = bTime - aTime;
            break;
          }
          case 'version':
            cmp = a.currentVersion.localeCompare(b.currentVersion);
            break;
          case 'assetId':
            cmp = (a.assetId || '').localeCompare(b.assetId || '');
            break;
        }
        return sortOrder === 'desc' ? -cmp : cmp;
      });

      return {
        data: enriched,
        meta: { total: enriched.length }
      };
    }
    return this.fetch<DeviceWithStatus[]>('/v1/devices', { params: params as unknown as Record<string, unknown> });
  }

  async getDevice(clientId: string): Promise<ApiResponse<DeviceDetail>> {
    if (this.useMock) {
      const device = this.universe.devices.find(d => d.clientId === clientId);
      if (!device) throw new Error(`Device not found: ${clientId}`);

      const enriched = enrichDeviceWithStatus(device);

      // Get activity from the universe generator
      const activity = this.universe.getActivityLog(clientId);

      // Get network history from the universe generator
      const networkHistory = this.universe.getNetworkHistory(clientId, 24);

      return { data: { ...enriched, activity, networkHistory } };
    }
    return this.fetch<DeviceDetail>(`/v1/devices/${clientId}`);
  }

  // ---------------------------------------------------------------------------
  // Device Health Snapshot
  // ---------------------------------------------------------------------------

  async getDeviceHealth(clientId: string): Promise<NetworkSnapshot | null> {
    if (this.useMock) {
      return null; // No mock health data
    }

    try {
      const headers: Record<string, string> = { 'Accept': 'application/json' };
      if (bearerToken) {
        headers['Authorization'] = `Bearer ${bearerToken}`;
      }

      const response = await fetch(`${this.baseUrl}/v1/devices/${clientId}/health`, {
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        // 404 = no snapshot yet, not an error
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.status}`);
      }

      const json = await response.json();
      return json.data as NetworkSnapshot;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Device Findings
  // ---------------------------------------------------------------------------

  async getDeviceFindings(clientId: string, params?: {
    severity?: string;
    category?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<FindingsResponse> {
    if (this.useMock) {
      return generateMockFindings(clientId);
    }

    try {
      const searchParams = new URLSearchParams();
      if (params?.severity) searchParams.set('severity', params.severity);
      if (params?.category) searchParams.set('category', params.category);
      if (params?.active !== undefined) searchParams.set('active', String(params.active));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const qs = searchParams.toString();

      const headers: Record<string, string> = { 'Accept': 'application/json' };
      if (bearerToken) {
        headers['Authorization'] = `Bearer ${bearerToken}`;
      }

      const response = await fetch(
        `${this.baseUrl}/v1/devices/${clientId}/findings${qs ? '?' + qs : ''}`,
        { headers, credentials: 'include' },
      );

      if (!response.ok) {
        if (response.status === 404) {
          return { data: [], meta: { total: 0, limit: 50, offset: 0 } };
        }
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json() as FindingsResponse;
    } catch {
      return { data: [], meta: { total: 0, limit: 50, offset: 0 } };
    }
  }

  // ---------------------------------------------------------------------------
  // Dashboard Stats
  // ---------------------------------------------------------------------------

  async getDashboardStats(mspId: string): Promise<ApiResponse<MspDashboardStats>> {
    if (this.useMock) {
      const devices = this.universe.devices.filter(d => d.mspId === mspId);
      const enriched = devices.map(enrichDeviceWithStatus);
      const orgs = this.universe.organizations.filter(o => o.mspId === mspId);

      const now = new Date();
      const stats: MspDashboardStats = {
        totalDevices: devices.length,
        totalOrgs: orgs.length,
        healthy: enriched.filter(d => d.status === 'healthy').length,
        warning: enriched.filter(d => d.status === 'warning').length,
        critical: enriched.filter(d => d.status === 'critical').length,
        onlineNow: devices.filter(d => {
          const lastSeenStr = d.lastSeen ?? d.lastCheckIn;
          const hours = (now.getTime() - new Date(lastSeenStr).getTime()) / (1000 * 60 * 60);
          return hours <= 1;
        }).length,
      };

      return { data: stats };
    }
    return this.fetch<MspDashboardStats>(`/v1/msp/${mspId}/stats`);
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private calculateOrgStats(orgId: string): OrgStats {
    const devices = this.universe.devices.filter(d => d.orgId === orgId);
    const enriched = devices.map(enrichDeviceWithStatus);
    const now = new Date();

    // Calculate network quality stats
    const nqStats = { good: 0, degraded: 0, poor: 0, offline: 0 };
    enriched.forEach(d => {
      if (!d.networkQuality) {
        nqStats.offline++;
      } else {
        nqStats[d.networkQuality.status]++;
      }
    });

    // Overall network status: worst status that affects > 0 devices
    let overallNq: NetworkQualityStatus = 'good';
    if (nqStats.offline > 0) overallNq = 'offline';
    else if (nqStats.poor > 0) overallNq = 'poor';
    else if (nqStats.degraded > 0) overallNq = 'degraded';

    // Calculate location breakdown
    const locations = { office: 0, home: 0, remote: 0, unknown: 0 };
    devices.forEach(d => {
      const locationType = d.location?.type ?? 'unknown';
      locations[locationType]++;
    });

    // Calculate connection state breakdown
    const connectionStates = { streaming: 0, idle: 0, offline: 0 };
    devices.forEach(d => {
      connectionStates[d.connectionState]++;
    });

    return {
      orgId,
      total: devices.length,
      healthy: enriched.filter(d => d.status === 'healthy').length,
      warning: enriched.filter(d => d.status === 'warning').length,
      critical: enriched.filter(d => d.status === 'critical').length,
      onlineNow: devices.filter(d => {
        const lastSeenStr = d.lastSeen ?? d.lastCheckIn;
        const hours = (now.getTime() - new Date(lastSeenStr).getTime()) / (1000 * 60 * 60);
        return hours <= 1;
      }).length,
      currentVersion: devices.filter(d => d.currentVersion === getLatestVersion()).length,
      networkQuality: {
        ...nqStats,
        overall: overallNq,
      },
      locations,
      connectionStates,
    };
  }

  // ---------------------------------------------------------------------------
  // API Credentials
  // ---------------------------------------------------------------------------

  async getCredentials(): Promise<{
    exists: boolean;
    clientId?: string;
    scopes?: string[];
    createdAt?: string;
    lastUsedAt?: string;
    enabled?: boolean;
    message?: string;
  }> {
    if (this.useMock) {
      return { exists: false, message: 'No API credentials yet. POST to this endpoint to generate them.' };
    }

    const response = await fetch(`${this.baseUrl}/v1/credentials`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch credentials: ${response.status}`);
    }

    return response.json();
  }

  async createCredentials(): Promise<{
    clientId: string;
    clientSecret: string;
    scopes: string[];
    message: string;
  }> {
    const response = await fetch(`${this.baseUrl}/v1/credentials`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to create credentials' }));
      throw new Error(err.error || err.message || 'Failed to create credentials');
    }

    return response.json();
  }

  async regenerateCredentials(): Promise<{
    clientId: string;
    clientSecret: string;
    message: string;
  }> {
    const response = await fetch(`${this.baseUrl}/v1/credentials/regenerate`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to regenerate credentials' }));
      throw new Error(err.error || err.message || 'Failed to regenerate credentials');
    }

    return response.json();
  }

  // ---------------------------------------------------------------------------
  // Device Logs
  // ---------------------------------------------------------------------------

  async getDeviceLogs(clientId: string, params?: {
    limit?: number;
    severity?: string;
    from?: string;
    to?: string;
  }): Promise<DeviceLogsResponse> {
    if (this.useMock) {
      let logs = this.universe.getDeviceLogs(clientId);
      if (params?.severity) {
        logs = logs.filter(l => l.severity === params.severity);
      }
      const limit = params?.limit ?? 100;
      logs = logs.slice(0, limit);
      return { data: logs, meta: { total: logs.length } };
    }

    const url = new URL(`${this.baseUrl}/v1/devices/${clientId}/logs`, window.location.origin);
    if (params?.limit) url.searchParams.set('limit', String(params.limit));
    if (params?.severity) url.searchParams.set('severity', params.severity);
    if (params?.from) url.searchParams.set('from', params.from);
    if (params?.to) url.searchParams.set('to', params.to);

    const logHeaders: Record<string, string> = { 'Accept': 'application/json' };
    if (bearerToken) {
      logHeaders['Authorization'] = `Bearer ${bearerToken}`;
    }

    const response = await fetch(url.toString(), {
      headers: logHeaders,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('Unauthorized - please log in again');
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ---------------------------------------------------------------------------
  // MSP Members
  // ---------------------------------------------------------------------------

  async getMspMembers(): Promise<MspMembersResponse> {
    if (this.useMock) {
      return { data: [], meta: { total: 0 } };
    }

    const memberHeaders: Record<string, string> = { 'Accept': 'application/json' };
    if (bearerToken) {
      memberHeaders['Authorization'] = `Bearer ${bearerToken}`;
    }

    const response = await fetch(`${this.baseUrl}/v1/msp/members`, {
      headers: memberHeaders,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('Unauthorized - please log in again');
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private async fetch<T>(
    path: string,
    options?: { params?: Record<string, unknown> }
  ): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (bearerToken) {
      headers['Authorization'] = `Bearer ${bearerToken}`;
    }

    const response = await fetch(url.toString(), {
      headers,
      credentials: 'include', // Include session cookies as fallback
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized - please log in again');
      }
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}

// Export singleton instance
export const api = new MspApiClient();

// Export config for components that need it
export { config as apiConfig };

// ---------------------------------------------------------------------------
// Mock Data: Findings
// ---------------------------------------------------------------------------

const mockFindingTemplates: Omit<Finding, 'id' | 'createdAt' | 'gatewayMAC' | 'networkName'>[] = [
  {
    severity: 'critical',
    category: 'gateway',
    technicalLabel: 'EOL Router',
    impactSummary: 'Router no longer receives security updates',
    description: 'The TP-Link Archer C7 v2 reached end-of-life and no longer receives firmware or security patches.',
    technicalDetails: 'Model: TP-Link Archer C7 v2. Last firmware: 2022-03-15. EOL date: 2023-06-01.',
    whyItMatters: 'Unpatched routers are vulnerable to known exploits that could compromise your entire network.',
    howToResolve: 'Replace this router with a currently supported model. We recommend checking the manufacturer website for current models.',
    source: 'Scan',
    actionability: 2,
    resolved: false,
    archived: false,
    isAutoFixable: false,
    canArchive: false,
    segment: 'homeNetwork',
  },
  {
    severity: 'warning',
    category: 'wifi',
    technicalLabel: 'Weak WiFi Signal',
    impactSummary: 'Signal strength is poor (-78 dBm)',
    description: 'The WiFi signal at this location is weak, which can cause slow speeds, dropped connections, and high latency.',
    technicalDetails: 'RSSI: -78 dBm, SNR: 12 dB. Threshold for reliable operation is -70 dBm.',
    whyItMatters: 'Weak signal forces the device to use slower modulation, reducing throughput and increasing retransmissions.',
    howToResolve: 'Move closer to the access point, reduce obstructions, or add a WiFi extender or mesh node.',
    source: 'WiFi Scan',
    actionability: 2,
    resolved: false,
    archived: false,
    isAutoFixable: false,
    canArchive: true,
    segment: 'wifi',
  },
  {
    severity: 'warning',
    category: 'security',
    technicalLabel: 'WPS Enabled',
    impactSummary: 'WiFi Protected Setup is active and vulnerable',
    description: 'WPS PIN mode is enabled on your router. This feature has known vulnerabilities that allow attackers to discover your WiFi password.',
    howToResolve: 'Disable WPS in your router settings under WiFi Security or Advanced settings.',
    source: 'WiFi Scan',
    actionability: 2,
    resolved: false,
    archived: false,
    isAutoFixable: false,
    canArchive: true,
    segment: 'homeNetwork',
  },
  {
    severity: 'info',
    category: 'isp',
    technicalLabel: 'IPv6 Not Available',
    impactSummary: 'ISP does not provide IPv6 connectivity',
    description: 'Your internet connection is IPv4-only. While not immediately impactful, some services may be faster over IPv6.',
    howToResolve: 'Contact your ISP to enable IPv6, or check your router settings to ensure IPv6 is not disabled.',
    source: 'Scan',
    actionability: 1,
    resolved: false,
    archived: false,
    isAutoFixable: false,
    canArchive: true,
    segment: 'isp',
  },
  {
    severity: 'warning',
    category: 'latency',
    technicalLabel: 'High Bufferbloat',
    impactSummary: 'Latency spikes to 180ms under load',
    description: 'When the connection is under load, latency increases significantly. This causes lag in video calls and gaming.',
    technicalDetails: 'Idle latency: 12ms. Under load: 180ms. Bufferbloat grade: D.',
    whyItMatters: 'High bufferbloat makes your connection feel slow even when you have plenty of bandwidth.',
    howToResolve: 'Enable SQM (Smart Queue Management) on your router if supported, or upgrade to a router with SQM support.',
    source: 'Deep Scan',
    actionability: 2,
    resolved: false,
    archived: false,
    isAutoFixable: false,
    canArchive: true,
    segment: 'homeNetwork',
  },
];

function generateMockFindings(clientId: string): FindingsResponse {
  // Use clientId to seed which findings appear (deterministic per device)
  const hash = clientId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const count = 2 + (hash % 4); // 2-5 findings per device
  const findings: Finding[] = [];

  for (let i = 0; i < count && i < mockFindingTemplates.length; i++) {
    const template = mockFindingTemplates[(hash + i) % mockFindingTemplates.length];
    findings.push({
      ...template,
      id: `mock-finding-${clientId.slice(0, 8)}-${i}`,
      createdAt: new Date(Date.now() - i * 86400000).toISOString(),
      gatewayMAC: 'AA:BB:CC:DD:EE:FF',
      networkName: 'Office WiFi',
    } as Finding);
  }

  return {
    data: findings,
    meta: { total: findings.length, limit: 50, offset: 0 },
  };
}
